import { WorkspaceStorageHeader } from "./WorkspaceStorageHeader";
import { useEffect, useMemo, useState } from "react";
import { Eraser, Search, Trash2 } from "lucide-react";
import type { WorkspaceStorageOverview } from "@lumen/shared";
import { api } from "../../api";
import { t } from "../../i18n";
import { formatBytes } from "../../format-bytes";

function formatDate(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

export function WorkspaceStoragePage({ canManage }: { canManage: boolean }) {
  const [overview, setOverview] = useState<WorkspaceStorageOverview | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setOverview(await api<WorkspaceStorageOverview>("/workspace-storage"));
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not load workspace storage usage",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const reclaimRoot = async (runtimeId: string) => {
    if (
      !window.confirm(
        `Permanently delete the workspace data of ${runtimeId}? The workspace is recreated fresh the next time it is used.`,
      )
    )
      return;
    setBusy(runtimeId);
    setError("");
    setNotice("");
    try {
      await api("/workspace-storage/reclaim", {
        method: "POST",
        body: JSON.stringify({ runtimeId }),
      });
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not reclaim the workspace",
      );
    } finally {
      setBusy("");
    }
  };

  const cleanUntracked = async () => {
    if (
      !window.confirm(
        "Reclaim every workspace root that is no longer tracked? This permanently deletes their files.",
      )
    )
      return;
    setBusy("gc");
    setError("");
    setNotice("");
    try {
      const result = await api<{ removed: number; skipped: number }>(
        "/workspace-storage/gc",
        { method: "POST", body: "{}" },
      );
      setNotice(
        `${result.removed} workspace root(s) reclaimed; ${result.skipped} skipped.`,
      );
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not reclaim untracked workspace roots",
      );
    } finally {
      setBusy("");
    }
  };

  const maintain = async (target: string, label: string) => {
    if (
      !window.confirm(
        `Clean ${label}? This frees space on the workspace volume; deploy tooling recreates what it needs.`,
      )
    )
      return;
    setBusy(target);
    setError("");
    setNotice("");
    try {
      const result = await api<{ freedBytes: number }>(
        "/workspace-storage/maintenance",
        { method: "POST", body: JSON.stringify({ target }) },
      );
      setNotice(`${label}: ${formatBytes(result.freedBytes)} freed.`);
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : `Could not clean ${label}`,
      );
    } finally {
      setBusy("");
    }
  };

  const runtimeRoots = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const rows = overview?.runtimeRoots ?? [];
    if (!normalizedQuery) return rows;
    return rows.filter((root) =>
      [
        root.runtimeId,
        root.backend,
        root.phase,
        root.workspaceName,
        root.conversationTitle,
        root.userName,
        root.tracked ? "tracked" : "untracked",
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedQuery)),
    );
  }, [overview, query]);

  const usedBytes =
    overview?.volume.totalBytes != null && overview.volume.freeBytes != null
      ? Math.max(0, overview.volume.totalBytes - overview.volume.freeBytes)
      : null;
  const usedPercent =
    usedBytes != null && overview?.volume.totalBytes
      ? Math.min(100, (usedBytes / overview.volume.totalBytes) * 100)
      : null;

  return (
    <section className="min-w-0 flex-1 overflow-y-auto p-5 md:p-8">
      <div className="mx-auto max-w-6xl">
        <WorkspaceStorageHeader
          title={t("Workspace storage")}
          description={t(
            "See what fills the workspace volume and reclaim space from workspaces and deployment scaffolding.",
          )}
          actions={
            canManage && (
              <button
                className="button"
                disabled={busy !== "" || loading}
                onClick={() => void cleanUntracked()}
              >
                <Trash2 size={15} />
                {t("Reclaim untracked")}
              </button>
            )
          }
          loading={loading}
          onRefresh={load}
          error={error}
        />
        {notice && (
          <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/20 dark:text-emerald-300">
            {notice}
          </div>
        )}

        {overview && (
          <div className="panel mb-5 p-5">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="font-semibold">{t("Workspace volume")}</h2>
              <span className="text-sm text-stone-500">
                {overview.volume.path}
              </span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-stone-200 dark:bg-white/10">
              <div
                className={`h-full rounded-full ${
                  usedPercent != null && usedPercent >= 90
                    ? "bg-red-500"
                    : "bg-lumen-500"
                }`}
                style={{ width: `${usedPercent ?? 0}%` }}
              />
            </div>
            <p className="mt-2 text-sm text-stone-500">
              {t("{{used}} used · {{free}} free · {{total}} total", {
                used: formatBytes(usedBytes),
                free: formatBytes(overview.volume.freeBytes),
                total: formatBytes(overview.volume.totalBytes),
              })}
              {overview.retentionDays > 0 &&
                ` · ${t("Untracked workspaces are reclaimed after {{days}} days", { days: overview.retentionDays })}`}
            </p>
          </div>
        )}

        {overview && (
          <div className="panel mb-5 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-stone-200 text-xs text-stone-500 uppercase dark:border-white/10">
                <tr>
                  <th className="p-3">{t("Store")}</th>
                  <th className="p-3">{t("Path")}</th>
                  <th className="p-3">{t("Size")}</th>
                  {canManage && <th className="p-3" />}
                </tr>
              </thead>
              <tbody>
                {overview.stores.map((store) => (
                  <tr
                    key={store.id}
                    className="border-b border-stone-100 last:border-0 dark:border-white/5"
                  >
                    <td className="p-3 font-medium">{store.label}</td>
                    <td className="p-3 text-stone-500">{store.path}</td>
                    <td className="p-3">
                      {store.present
                        ? formatBytes(store.sizeBytes)
                        : t("absent")}
                    </td>
                    {canManage && (
                      <td className="p-3 text-right">
                        {store.removable && (
                          <button
                            className="button text-xs"
                            disabled={busy !== "" || !store.present}
                            onClick={() => void maintain(store.id, store.label)}
                          >
                            <Eraser size={14} />
                            {t("Clean")}
                          </button>
                        )}
                        {store.id === "docker" && (
                          <button
                            className="button text-xs"
                            disabled={busy !== ""}
                            onClick={() =>
                              void maintain("docker-prune", store.label)
                            }
                          >
                            <Eraser size={14} />
                            {t("Prune")}
                          </button>
                        )}
                        {store.id === "podman-store" && (
                          <button
                            className="button text-xs"
                            disabled={busy !== ""}
                            onClick={() =>
                              void maintain("podman-prune", store.label)
                            }
                          >
                            <Eraser size={14} />
                            {t("Prune")}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {overview && (
          <div className="panel overflow-x-auto">
            <div className="flex flex-wrap items-center justify-between gap-3 p-3">
              <h2 className="font-semibold">{t("Workspace runtimes")}</h2>
              <div className="relative min-w-[240px] flex-1 sm:max-w-xs">
                <Search
                  size={15}
                  className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-stone-400"
                />
                <input
                  className="w-full rounded-lg border border-stone-200 bg-white py-2 pr-3 pl-9 text-sm transition outline-none focus:border-lumen-500 dark:border-white/10 dark:bg-white/5"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t("Search runtimes")}
                  aria-label={t("Search runtimes")}
                />
              </div>
            </div>
            <table className="w-full text-left text-sm">
              <thead className="border-b border-stone-200 text-xs text-stone-500 uppercase dark:border-white/10">
                <tr>
                  <th className="p-3">{t("Runtime")}</th>
                  <th className="p-3">{t("Used by")}</th>
                  <th className="p-3">{t("Phase")}</th>
                  <th className="p-3">{t("Created")}</th>
                  <th className="p-3">{t("Size")}</th>
                  <th className="p-3">{t("Status")}</th>
                  {canManage && <th className="p-3" />}
                </tr>
              </thead>
              <tbody>
                {runtimeRoots.map((root) => (
                  <tr
                    key={root.runtimeId}
                    className="border-b border-stone-100 last:border-0 dark:border-white/5"
                  >
                    <td className="p-3 font-mono text-xs">{root.runtimeId}</td>
                    <td className="p-3">
                      {root.workspaceName ?? t("Untracked workspace")}
                      {root.userName && (
                        <span className="block text-xs text-stone-500">
                          {root.userName}
                          {root.conversationTitle
                            ? ` · ${root.conversationTitle}`
                            : ""}
                        </span>
                      )}
                    </td>
                    <td className="p-3">{root.phase ?? "—"}</td>
                    <td className="p-3">{formatDate(root.createdAt)}</td>
                    <td className="p-3">{formatBytes(root.sizeBytes)}</td>
                    <td className="p-3">
                      {root.tracked ? t("Tracked") : t("Untracked")}
                    </td>
                    {canManage && (
                      <td className="p-3 text-right">
                        <button
                          className="button text-xs"
                          disabled={busy !== ""}
                          onClick={() => void reclaimRoot(root.runtimeId)}
                        >
                          <Trash2 size={14} />
                          {t("Reclaim")}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!runtimeRoots.length && (
                  <tr>
                    <td
                      className="p-4 text-center text-stone-500"
                      colSpan={canManage ? 7 : 6}
                    >
                      {t("No workspace runtimes on disk")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {loading && (
          <p className="mt-4 text-sm text-stone-500">{t("Loading…")}</p>
        )}
      </div>
    </section>
  );
}
